import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';
import { categoriesData } from '../../data/companyData';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};

export const ProductsSection = () => {
  // Show first 6 categories
  const displayCategories = categoriesData.slice(0, 6);

  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="products-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#001582]/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-[#001582] text-sm font-semibold uppercase tracking-wider">Produk</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-4">
              Produk <span className="text-[#001582]">Unggulan</span> Kami
            </h2>
            <p className="text-slate-600 text-lg">
              Berbagai komponen dan equipment industri berkualitas tinggi untuk mendukung operasional Anda.
            </p>
          </div>
          <Link to="/produk-layanan">
            <Button 
              variant="outline"
              className="border-2 border-[#001582] text-[#001582] hover:bg-[#001582] hover:text-white font-semibold rounded-xl"
              data-testid="products-view-all"
            >
              Lihat Semua
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </motion.div>

        {/* Categories Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {displayCategories.map((category) => (
            <motion.div
              key={category.id}
              variants={itemVariants}
              className="group"
            >
              <Link to={`/produk-layanan/${category.id}`} className="block">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img 
                      src={category.image}
                      alt={category.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#001582]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-end p-4">
                      <div className="w-10 h-10 bg-[#FFC107] rounded-full flex items-center justify-center">
                        <ArrowUpRight className="w-5 h-5 text-[#001582]" />
                      </div>
                    </div>
                    {/* Item count badge */}
                    <div className="absolute top-4 right-4">
                      <span className="px-2 py-1 bg-[#FFC107] text-[#001582] text-xs font-bold rounded-full">
                        {category.items.length} Item
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-heading text-lg font-bold text-[#1a1a1a] mb-2 group-hover:text-[#001582] transition-colors">
                      {category.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-2">
                      {category.description}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;
